import { Festival, UnwrapPromise } from "@prisma/client";
import { isFuture } from "date-fns";
import { FiCalendar, FiEdit2 } from "react-icons/fi";
import { LoaderFunction, Outlet, useMatches } from "remix";
import { BreadcrumbLink } from "~/components/Breadcrumbs";
import { EditButton } from "~/components/Button";
import { ImageComponent } from "~/components/ImageComponent";
import { NavigationLink } from "~/components/NavigationLink";
import { SponsorDialog } from "~/components/SponsorDialog";
import { getFestivalSponsors, requireFestival } from "~/utils/festival.server";
import { requireUserId } from "~/utils/session.server";
import { Unpacked } from "~/utils/types";
import { useIsLoggedIn } from "~/utils/useIsLoggedIn";

type LoaderData = {
  festival?: Festival;
  sponsors: UnwrapPromise<ReturnType<typeof getFestivalSponsors>>;
};

export const handle = {
  breadcrumb: ({
    params,
    data,
  }: {
    params: Record<string, string>;
    data?: LoaderData;
  }) => {
    return data?.festival?.name ? (
      <BreadcrumbLink to={`/${params.festivalSlug}`}>
        {data.festival.name}
      </BreadcrumbLink>
    ) : null;
  },
  footer: ({ data }: Unpacked<ReturnType<typeof useMatches>>) => {
    return <FooterLogos {...(data as LoaderData)} />;
  },
  navLinks: (
    { pathname, data }: Unpacked<ReturnType<typeof useMatches>>,
    isLoggedIn: boolean
  ) =>
    data ? (
      <>
        <NavigationLink to={pathname} end>
          Info
        </NavigationLink>

        <NavigationLink to={`${pathname}/events`}>
          <FiCalendar className="mr-2" />
          Kalender
        </NavigationLink>

        <NavigationLink to={`${pathname}/info-staende`}>
          Infostände
        </NavigationLink>

        <NavigationLink to={`${pathname}/menschen`}>Menschen</NavigationLink>
      </>
    ) : null,
};

function FooterLogos({ sponsors, festival }: LoaderData) {
  const isLoggedIn = useIsLoggedIn();

  return (
    <>
      {!!sponsors && (
        <>
          <p className="text-gray-500 uppercase text-center">Präsentiert von</p>
          {!!isLoggedIn && !!festival?.id && (
            <div className="mx-auto">
              <SponsorDialog
                festivalId={festival.id}
                onFinished={() => document.location.reload()}
              >
                <EditButton>Add Sponsor</EditButton>
              </SponsorDialog>
            </div>
          )}
          <ul className="flex items-center justify-center flex-row flex-wrap gap-12 my-12">
            {sponsors.map((sponsor) => (
              <li key={sponsor.id} className="relative">
                <a
                  className="inline-block saturate-0 hover:saturate-100"
                  href={sponsor.url}
                  title={sponsor.name}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                >
                  <ImageComponent
                    showCopyright={false}
                    className="w-[160px] max-w-full object-contain"
                    width="160px"
                    height="160px"
                    image={sponsor.image}
                  />
                </a>
                {!!isLoggedIn && !!festival?.id && (
                  <>
                    <SponsorDialog festivalId={festival.id} sponsor={sponsor}>
                      <EditButton className="absolute -right-1 -top-1">
                        <FiEdit2 />
                      </EditButton>
                    </SponsorDialog>
                  </>
                )}
              </li>
            ))}
            {/* <li>
          <a
            className="inline-block saturate-0 hover:saturate-100"
            href="https://stadt.muenchen.de/rathaus/verwaltung/kulturreferat.html"
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            <img
              alt="Dieses Projekt wird gefördert von der Landeshauptstadt München"
              className="w-[160px] max-w-full object-contain"
              src="/footer-logos/kulturreferat_black.png"
            />
          </a>
        </li>
        <li>
          <a
            className="inline-block saturate-0 hover:saturate-100"
            href="https://www.glockenbachwerkstatt.de/"
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            <img
              alt="Logo der Glockenbachwerkstatt München"
              className="w-[160px] max-w-full object-contain"
              src="/footer-logos/Glockenbachwerkstatt_Logo_RGB_standard.png"
            />
          </a>
        </li>
        <li>
          <a
            className="inline-block saturate-0 hover:saturate-100"
            href="https://bellevuedimonaco.de/"
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            <img
              alt="Logo des Bellevue di Monacos München"
              className="w-[160px] max-w-full object-contain"
              src="/footer-logos/Bellevue_LOGO_black.png"
            />
          </a>
        </li>
        <li>
          <a
            className="inline-block saturate-0 hover:saturate-100"
            href="https://www.baysem.de/"
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            <img
              alt="Logo des Bayerischen Seminar für Politik e.V."
              className="w-[160px] max-w-full object-contain"
              src="/footer-logos/BAYSEM-final.png"
            />
          </a>
        </li>
        <li>
          <a
            className="inline-block saturate-0 hover:saturate-100"
            href="https://nikamusic.de/"
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            <img
              alt="Logo von NiKa Music"
              className="w-[160px] max-w-full object-contain"
              src="/footer-logos/Nika_Logo_Neu.jpeg"
            />
          </a>
        </li>
        <li>
          <a
            className="inline-block saturate-0 hover:saturate-100"
            href="https://holyfingers.events/"
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            <img
              alt="Logo von Holy Fingers Events"
              className="w-[160px] max-w-full object-contain"
              src="/footer-logos/Holy_Fingers_Logo.jpg"
            />
          </a>
        </li> */}
          </ul>
        </>
      )}
    </>
  );
}

export const loader: LoaderFunction = async ({ params, request }) => {
  const festival = await requireFestival(params.festivalSlug);
  const sponsors = await getFestivalSponsors(festival.id);

  if (!festival.publishedAt || isFuture(festival.publishedAt)) {
    await requireUserId(request);
  }

  return { festival, sponsors } as LoaderData;
};

export default function Festival() {
  return (
    <>
      <div className="main-layout-centered">
        <Outlet />
      </div>
    </>
  );
}
